import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Comic, ComicSeries, isRead, isStarted } from '../types';
import { brand, gradients } from '../../../theme/colors';

interface IssueChecklistProps {
  series: ComicSeries;
  // This series' comics
  comics: Comic[];
  // Numbered slots to show before any extras
  size: number;
  isReadOnly: boolean;
  onToggleNumber: (series: ComicSeries, number: number) => void;
  onMarkRange: (series: ComicSeries, upTo: number) => void;
}

type SlotState = 'read' | 'started' | 'owned' | 'logged' | 'missing';

const SLOT_LABELS: Record<SlotState, string> = {
  read: 'Read',
  started: 'Started',
  owned: 'Owned, not read',
  logged: 'Not read',
  missing: 'Not added',
};

const slotSx = (state: SlotState) => {
  switch (state) {
    case 'read':
      return { backgroundImage: gradients.primary, color: '#fff', border: '1px solid transparent' };
    // Half filled: the gradient across the lower left
    case 'started':
      return {
        backgroundImage: `linear-gradient(135deg, transparent 50%, ${brand.pink} 50%, ${brand.purple})`,
        border: `1.5px solid ${brand.pink}`,
        color: 'text.primary',
      };
    case 'owned':
      return { border: `1.5px solid ${brand.pink}`, color: 'text.primary' };
    case 'logged':
      return { border: '1px solid', borderColor: 'divider', color: 'text.primary' };
    default:
      return { border: '1px dashed', borderColor: 'divider', color: 'text.disabled' };
  }
};

// One tile per issue (or volume). Each tap moves it on: not read → started
// → read → not read. Tapping a number that isn't in the library yet adds it,
// started.
const IssueChecklist = ({
  series,
  comics,
  size,
  isReadOnly,
  onToggleNumber,
  onMarkRange,
}: IssueChecklistProps) => {
  const [extra, setExtra] = useState(0);
  const [upTo, setUpTo] = useState('');

  useEffect(() => {
    setExtra(0);
    setUpTo('');
  }, [series.id]);

  const prefix = series.trackBy === 'issue' ? '#' : 'Vol. ';
  const count = size + extra;

  const byNumber = new Map<number, Comic>();
  comics.forEach((comic) => {
    if (comic.number != null && Number.isInteger(comic.number)) byNumber.set(comic.number, comic);
  });

  const stateOf = (number: number): SlotState => {
    const comic = byNumber.get(number);
    if (!comic) return 'missing';
    if (isRead(comic)) return 'read';
    if (isStarted(comic)) return 'started';
    return comic.owned ? 'owned' : 'logged';
  };

  const rangeValue = parseInt(upTo, 10);
  const canMarkRange = Number.isInteger(rangeValue) && rangeValue >= 1 && rangeValue <= 999;

  const markRange = () => {
    if (!canMarkRange) return;
    onMarkRange(series, rangeValue);
    if (rangeValue > count) setExtra((prev) => prev + rangeValue - count);
    setUpTo('');
  };

  return (
    <Box>
      {count === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {isReadOnly
            ? 'Nothing logged yet.'
            : `How far have you got? Mark a range below, or add ${prefix.trim()} 1.`}
        </Typography>
      ) : (
        <Box
          role="group"
          aria-label={`${series.title} checklist`}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
            gap: 0.75,
            mb: 1.5,
          }}
        >
          {Array.from({ length: count }, (_, index) => index + 1).map((number) => {
            const state = stateOf(number);
            const owned = Boolean(byNumber.get(number)?.owned);
            const label = `${prefix}${number} · ${SLOT_LABELS[state]}${owned && state === 'read' ? ' · Owned' : ''}`;
            return (
              <Tooltip key={number} title={label} enterDelay={400}>
                <ButtonBase
                  aria-label={label}
                  aria-pressed={state === 'read' ? true : state === 'started' ? 'mixed' : false}
                  disabled={isReadOnly}
                  onClick={() => onToggleNumber(series, number)}
                  sx={{
                    position: 'relative',
                    height: 40,
                    borderRadius: 1.5,
                    fontSize: 13,
                    fontWeight: 800,
                    transition: 'transform 0.1s ease',
                    '&:hover': { transform: 'translateY(-1px)' },
                    ...slotSx(state),
                  }}
                >
                  {number}
                  {/* Owned: a dot in the corner, read or not */}
                  {owned && (
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: state === 'read' ? '#fff' : brand.pink,
                      }}
                    />
                  )}
                </ButtonBase>
              </Tooltip>
            );
          })}
          {!isReadOnly && (
            <Tooltip title={`Add ${prefix}${count + 1}`} enterDelay={400}>
              <ButtonBase
                aria-label={`Show ${prefix}${count + 1}`}
                onClick={() => setExtra((prev) => prev + 1)}
                sx={{ height: 40, borderRadius: 1.5, ...slotSx('missing'), color: 'text.secondary' }}
              >
                <AddIcon fontSize="small" />
              </ButtonBase>
            </Tooltip>
          )}
        </Box>
      )}

      {!isReadOnly && (
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            markRange();
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Typography variant="body2" color="text.secondary" noWrap>
            Read up to
          </Typography>
          <TextField
            size="small"
            type="number"
            value={upTo}
            onChange={(e) => setUpTo(e.target.value)}
            inputProps={{ min: 1, max: 999, 'aria-label': 'Last one read' }}
            sx={{ width: 72, flexShrink: 0 }}
          />
          <Button
            type="submit"
            size="small"
            variant="outlined"
            disabled={!canMarkRange}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Mark read
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default IssueChecklist;
